import React, { useEffect, useMemo, useState } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import FileTypeModal from "./FileTypeModal";
import { getFileExtension, getFileName, resolveFileUrl } from "./fileUtils";

const officePreviewUrl = (uri) => `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(uri || "")}`;

const DocumentFileViewer = ({ file, open, onClose, projectId }) => {
  const fileName = getFileName(file);
  const fileType = getFileExtension(file);
  const uri = resolveFileUrl(file, projectId);

  const [sheetRows, setSheetRows] = useState([]);
  const [textContent, setTextContent] = useState("");
  const [docxHtml, setDocxHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const documents = [{ uri, fileType, fileName }];
  const isImage = useMemo(() => ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(fileType), [fileType]);
  const isSheet = useMemo(() => ["xls", "xlsx", "csv"].includes(fileType), [fileType]);
  const isPdf = fileType === "pdf";
  const isText = useMemo(() => ["txt", "rtf", "md"].includes(fileType), [fileType]);
  const isDocx = fileType === "docx";
  const isOfficeEmbed = useMemo(() => ["doc", "ppt", "pptx"].includes(fileType), [fileType]);

  useEffect(() => {
    if (!open || !uri) return;

    setError("");
    setLoading(false);
    setSheetRows([]);
    setTextContent("");
    setDocxHtml("");

    const fetchSheet = async () => {
      setLoading(true);
      try {
        const response = await fetch(uri);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const arrayBuffer = await response.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, { type: "array" });
        const firstSheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        setSheetRows(rows);
      } catch (e) {
        setError("Failed to open spreadsheet.");
      } finally {
        setLoading(false);
      }
    };

    const fetchText = async () => {
      setLoading(true);
      try {
        const response = await fetch(uri);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        setTextContent(text);
      } catch (e) {
        setError("Failed to open text file.");
      } finally {
        setLoading(false);
      }
    };

    const fetchDocx = async () => {
      setLoading(true);
      try {
        const response = await fetch(uri);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxHtml(result.value || "");
      } catch (e) {
        setError("Failed to open DOCX in browser preview.");
      } finally {
        setLoading(false);
      }
    };

    if (isSheet) fetchSheet();
    if (isText) fetchText();
    if (isDocx) fetchDocx();
  }, [open, uri, isSheet, isText, isDocx]);

  return (
    <FileTypeModal open={open} onClose={onClose} title={fileName || "Document"}>
      {isImage ? (
        <div className="h-[70vh] flex items-center justify-center overflow-auto">
          <img src={uri} alt={fileName} className="max-h-full max-w-full object-contain" />
        </div>
      ) : null}

      {isPdf ? (
        <iframe title={fileName} src={uri} className="h-[70vh] w-full border-0" />
      ) : null}

      {isSheet ? (
        <div className="h-[70vh] overflow-auto">
          {loading ? <p className="text-sm">Loading...</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          {!loading && !error && sheetRows.length > 0 ? (
            <table className="min-w-full border-collapse text-sm">
              <tbody>
                {sheetRows.map((row, rIndex) => (
                  <tr key={rIndex}>
                    {(row || []).map((cell, cIndex) => (
                      <td key={`${rIndex}-${cIndex}`} className="border px-2 py-1">
                        {String(cell ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}

      {isText ? (
        <div className="h-[70vh] overflow-auto">
          {loading ? <p className="text-sm">Loading...</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          {!loading && !error ? <pre className="whitespace-pre-wrap text-sm">{textContent}</pre> : null}
        </div>
      ) : null}

      {isDocx ? (
        <div className="h-[70vh] overflow-auto">
          {loading ? <p className="text-sm">Loading...</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          {!loading && !error && docxHtml ? (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: docxHtml }} />
          ) : null}
        </div>
      ) : null}

      {isOfficeEmbed ? (
        <div className="h-[70vh] space-y-2">
          <p className="text-xs text-gray-500">
            Embedded Office preview depends on external Office viewer availability.
          </p>
          <iframe title={fileName} src={officePreviewUrl(uri)} className="h-[66vh] w-full border-0" />
        </div>
      ) : null}

      {!isImage && !isPdf && !isSheet && !isText && !isDocx && !isOfficeEmbed ? (
        <div className="h-[70vh] overflow-auto">
          <DocViewer documents={documents} pluginRenderers={DocViewerRenderers} />
        </div>
      ) : null}

      <div className="mt-3 flex gap-3">
        <a
          href={uri}
          target="_blank"
          rel="noreferrer"
          className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Open File
        </a>
        <a
          href={uri}
          download={fileName}
          className="rounded bg-gray-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
        >
          Download
        </a>
      </div>
    </FileTypeModal>
  );
};

export default DocumentFileViewer;
